import { LocationType, useFrontendSubscriptionStatus } from '@cmctechnology/webinvest-store-frontend';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ORDER_API_NAMES } from './apis';
import {
  makeAccountOrderHistoryApiKey,
  makeAccountOrderHistorySubscriptionKey,
  OrderHistoryArgs,
  subscribeOrderHistory,
  unsubscribeOrderHistory
} from './orderHistoryActions';
import { subscribeOrders, unsubscribeOrders } from './ordersActions';
import { IStore, ORDERS_STORE_NAME } from './store';
import { AccountKey } from './types';
import isEqual from 'lodash.isequal';

export const useSubscribeOrders = (location: LocationType, accountNo: AccountKey) => {
  const dispatch = useDispatch();
  const orders = useSelector((store: IStore) => store[ORDERS_STORE_NAME].orders[accountNo]);
  const { polling, error, fetching } = useFrontendSubscriptionStatus(ORDERS_STORE_NAME, ORDER_API_NAMES.OpenOrdersLeanGet2, accountNo);
  const [prevAccountNo, setPrevAccountNo] = useState<AccountKey>(accountNo);

  useEffect(() => {
    dispatch<any>(subscribeOrders(location, accountNo));
    return () => {
      dispatch<any>(unsubscribeOrders(location, accountNo));
    };
  }, []);

  useEffect(() => {
    if (accountNo === prevAccountNo) return;

    dispatch<any>(unsubscribeOrders(location, prevAccountNo));
    dispatch<any>(subscribeOrders(location, accountNo));
    setPrevAccountNo(accountNo);
    return () => {
      dispatch<any>(unsubscribeOrders(location, accountNo));
    };
  }, [accountNo]);

  return {
    loading: fetching,
    polling,
    error,
    data: orders,
    fetching
  };
};

export const useSubscribeOrderHistory = (location: LocationType, accountNo: AccountKey, from: Date, to: Date, pageSize: number, page: number) => {
  const dispatch = useDispatch();
  const history = useSelector(
    (store: IStore) => store[ORDERS_STORE_NAME].history[accountNo]?.from[from.valueOf()]?.to[to.valueOf()]?.pageSize[pageSize]?.page[page]
  );
  const pageCount = useSelector(
    (store: IStore) => store[ORDERS_STORE_NAME].history[accountNo]?.from[from.valueOf()]?.to[to.valueOf()]?.pageSize[pageSize]?.pageCount
  );
  const resultCount = useSelector(
    (store: IStore) => store[ORDERS_STORE_NAME].history[accountNo]?.from[from.valueOf()]?.to[to.valueOf()]?.pageSize[pageSize]?.resultCount
  );
  const { error, fetching } = useFrontendSubscriptionStatus(
    ORDERS_STORE_NAME,
    ORDER_API_NAMES.OrderHistoryGet3,
    makeAccountOrderHistoryApiKey(accountNo, from, to, pageSize, page)
  );
  const { polling } = useFrontendSubscriptionStatus(
    ORDERS_STORE_NAME,
    ORDER_API_NAMES.OrderHistoryGet3,
    makeAccountOrderHistorySubscriptionKey(accountNo, from, to, pageSize, page)
  );
  const [prevArgs, setPrevArgs] = useState<OrderHistoryArgs>();
  const args: OrderHistoryArgs = { accountNo, from: from.valueOf(), to: to.valueOf(), pageSize, page };

  useEffect(() => {
    dispatch<any>(subscribeOrderHistory(location, accountNo, from, to, pageSize, page));
    setPrevArgs(args);
    return () => {
      dispatch<any>(unsubscribeOrderHistory(location, accountNo, from, to, pageSize, page));
    };
  }, []);

  useEffect(() => {
    if (prevArgs == undefined || isEqual(args, prevArgs)) return;

    dispatch<any>(unsubscribeOrderHistory(location, prevArgs.accountNo, new Date(prevArgs.from), new Date(prevArgs.to), prevArgs.pageSize, prevArgs.page));
    dispatch<any>(subscribeOrderHistory(location, accountNo, from, to, pageSize, page));
    setPrevArgs(args);
    return () => {
      dispatch<any>(unsubscribeOrderHistory(location, accountNo, from, to, pageSize, page));
    };
  }, [accountNo, from, to, pageSize, page]);

  return {
    loading: fetching,
    polling,
    error,
    data: { history, pageCount, resultCount },
    fetching
  };
};
